import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import settingsService from "api/settingsService";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import { IPublicAddressBookStatus } from "dto/Settings/IPublicAddressBookStatus";
import { isValidNip } from "utils/IsValidNip";
import { ENotificationType, notification } from "utils/notification";
import { getErrorMessage } from "utils/getErrorMessage";
import styles from "../styles.module.scss";
import { useWizard } from "../WizardContext";

const Step7AddressBook = () => {
  const { t } = useTranslation();
  const { settings, patchSettings, goBack, goNext } = useWizard();

  const sharePublic = settings.sharePublic ?? false;
  const shareInternal = settings.shareInternal ?? false;
  const showPrivateName = settings.showPrivateName ?? false;
  const showBusinessCard = settings.showBusinessCard ?? false;
  const shareAddress = settings.shareAddress ?? true;

  const scopeSelected = sharePublic || shareInternal;
  const hasBusinessProfile =
    isValidNip(settings.nip ?? "") && !!(settings.companyName ?? "").length;

  const {
    data: addressBookData,
    refetch: refetchAddressBook,
    isLoading: isLoadingAddressBook,
  } = settingsService.useGetPublicAddressBookStatus();
  const { mutate: addToAddressBook, isLoading: isPublishing } =
    settingsService.useAddToPublicAddressBook();
  const { mutate: removeFromAddressBook, isLoading: isRemoving } =
    settingsService.useRemoveFromPublicAddressBook();

  const [status, setStatus] = useState<IPublicAddressBookStatus | null>(null);
  useEffect(() => {
    if (!addressBookData) return;
    const s = (addressBookData.data as IPublicAddressBookStatus) ?? null;
    setStatus(s);
    if (s) {
      patchSettings({
        nip: s.Nip ?? "",
        companyName: s.CompanyName ?? "",
        companyAddress: s.Address ?? "",
        shareAddress: !!s.ShareAddress,
        sharePublic: !!s.SharePublic,
        shareInternal: !!s.ShareInternal,
        businessMailboxNumber: s.MailboxNumber ?? "",
        privateMailboxNumber: s.PrivateMailboxNumber ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressBookData]);

  const isPublished = !!status;

  const handlePublish = () => {
    addToAddressBook(
      {
        Nip: settings.nip ?? "",
        WorkspaceId: settings.WorkspaceId,
        ShareAddress: shareAddress,
        SharePublic: sharePublic,
        ShareInternal: shareInternal,
        RegistryOfficeUserIds: settings.RegistryOfficeUserIds ?? [],
      },
      {
        onSuccess: () => {
          refetchAddressBook();
          goNext();
        },
        onError: (e: any) => {
          const msg =
            getErrorMessage(e?.response?.data) || t("errors.unknownError");
          notification({
            title: t("contactFormWizard.steps.addressBook.publishCta"),
            text: msg,
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  const handleRemove = () => {
    if (!status) return;
    removeFromAddressBook(
      { Id: status.Id },
      {
        onSuccess: () => {
          setStatus(null);
          refetchAddressBook();
        },
      },
    );
  };

  const canPublishPrivate = scopeSelected && showPrivateName;
  const canPublishBusiness =
    scopeSelected && showBusinessCard && hasBusinessProfile;
  const canPublish = canPublishPrivate || canPublishBusiness;

  return (
    <>
      {/* Wizytówka dla profilu prywatnego */}
      <div className={styles.cardProfileGroup}>
        <div className={styles.cardProfileLabel}>
          Wizytówka dla profilu prywatnego
        </div>
        <FormControl className={styles.addressBookCheckboxes}>
          <FormGroup>
            <FormControlLabel
              disabled={!scopeSelected || isPublished}
              control={
                <Checkbox
                  checked={showPrivateName}
                  onChange={(e) =>
                    patchSettings({ showPrivateName: e.target.checked })
                  }
                />
              }
              label="Udostępnij wizytówkę"
            />
          </FormGroup>
        </FormControl>
      </div>

      {isPendingVerification && (
        <div className={styles.pendingBanner}>
          {t("contactFormWizard.steps.addressBook.pendingVerification")}
        </div>
      )}

      {/* Wizytówka dla profilu biznesowego */}
      <div className={styles.cardProfileGroup}>
        <div className={styles.cardProfileLabel}>
          Wizytówka dla profilu biznesowego
        </div>

        {!hasBusinessProfile ? (
          <div className={styles.businessProfileHint}>
            Profil biznesowy nie jest aktywny. Przejdź do zakładki{" "}
            <strong>Konto i profile</strong> i uzupełnij dane firmy (NIP,
            nazwa), aby móc udostępniać wizytówkę biznesową.
          </div>
        ) : (
          <FormControl className={styles.addressBookCheckboxes}>
            <FormGroup>
              <FormControlLabel
                disabled={!scopeSelected || isPublished}
                control={
                  <Checkbox
                    checked={showBusinessCard}
                    onChange={(e) =>
                      patchSettings({ showBusinessCard: e.target.checked })
                    }
                  />
                }
                label="Udostępnij wizytówkę"
              />
              <FormControlLabel
                disabled={!scopeSelected || isPublished}
                control={
                  <Checkbox
                    checked={shareAddress}
                    onChange={(e) =>
                      patchSettings({ shareAddress: e.target.checked })
                    }
                  />
                }
                label={t("contactFormWizard.steps.addressBook.showAddress")}
              />
            </FormGroup>
          </FormControl>
        )}
      </div>

      {!scopeSelected && (
        <div className={styles.scopeHint}>
          Wróć do kroku 1 i wskaż zakres udostępnienia, aby aktywować opcje.
        </div>
      )}

      <div className={styles.stepActions}>
        <HollowButton text={t("common.back")} onClick={goBack} />
        {!isPublished ? (
          <Button
            text={t("contactFormWizard.steps.addressBook.publishCta")}
            onClick={handlePublish}
            disabled={!canPublish}
            loading={isPublishing || isLoadingAddressBook}
          />
        ) : (
          <HollowButton
            text={t("contactFormWizard.steps.addressBook.removeCta")}
            onClick={handleRemove}
            disabled={isRemoving}
          />
        )}
      </div>
    </>
  );
};

export default Step7AddressBook;
